import { SearchOutlined } from '@ant-design/icons'
import { Texto, Vertical } from '@gravitate-js/excalibrr'
import { Empty, Input, theme } from 'antd'
import { useMemo, useState } from 'react'
import {
  type GroupableThread,
  type SectionKey,
  groupThreads,
} from '../conversationGroups'
import { ConversationListItem } from './ConversationListItem'

export type ConversationsPanelProps = {
  threads: GroupableThread[]
  activeId: string | null
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  now?: Date
}

const SECTION_LABELS: Record<SectionKey, string> = {
  pinned: 'Pinned',
  today: 'Today',
  yesterday: 'Yesterday',
  lastWeek: 'Last Week',
  earlier: 'Earlier',
}

export function ConversationsPanel({
  threads,
  activeId,
  onSelect,
  onDelete,
  now,
}: ConversationsPanelProps) {
  const { token } = theme.useToken()
  const [search, setSearch] = useState('')
  const effectiveNow = useMemo(() => now ?? new Date(), [now])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return threads
    return threads.filter((th) => th.title.toLowerCase().includes(q))
  }, [threads, search])

  const sections = useMemo(
    () => groupThreads(filtered, effectiveNow),
    [filtered, effectiveNow],
  )

  function renderBody() {
    if (threads.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='No conversations yet'
        />
      )
    }
    if (sections.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='No conversations match your search'
        />
      )
    }
    return sections.map((section) => (
      <div key={section.key}>
        <Texto
          weight='bold'
          style={{
            display: 'block',
            padding: '8px 12px 4px',
            fontSize: 11,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: token.colorTextTertiary,
          }}
        >
          {SECTION_LABELS[section.key]}
        </Texto>
        {section.threads.map((th) => (
          <ConversationListItem
            key={th.id}
            thread={th}
            active={th.id === activeId}
            now={effectiveNow}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        ))}
      </div>
    ))
  }

  return (
    <Vertical gap={8} style={{ height: '100%' }}>
      <div style={{ padding: '8px 12px' }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder='Search by title or message…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>{renderBody()}</div>
    </Vertical>
  )
}
